import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { NavLink } from "react-router-dom";
import bootstrap from "bootstrap/dist/js/bootstrap.bundle.min.js";
import useUsers from "../../hooks/utilisateur/useUsers";
import { useHasAccess } from "../../utils/valideAccess";
import accessibleLinks from "../../constant/menuSidebar";
import { useAlertContext } from "../../contexts/AlertContext";
import "../../assets/css/sidebar.css";

function Sidebar() {
  const { logout } = useContext(AuthContext);
  const { unreadCount, markAllAsRead } = useAlertContext();

  const [user, setUser] = useState(null);
  const [allowedLinks, setAllowedLinks] = useState([]);

  const { getOneUser } = useUsers();
  const hasAccess = useHasAccess();
  const urlProfile = process.env.REACT_APP_API_URL_PROFILE;
  const defaultProfile = `${urlProfile}/default.png`;

  useEffect(() => {
    const userLocal = JSON.parse(localStorage.getItem("user"));
    if (userLocal && userLocal._id) {
      getOneUser(userLocal._id, (data) => {
        setUser(data);
      });
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    const filteredLinks = accessibleLinks.filter((link) =>
      hasAccess(user, link.permission),
    );
    setAllowedLinks(filteredLinks);
  }, [user, hasAccess]);

  // Réutilise le module déjà exécuté par l'import side-effect de index.js
  // (même chemin exact => webpack ne l'instancie qu'une seule fois, pas de
  // second runtime Bootstrap contrairement à l'import du paquet "bootstrap").
  const closeSidebarIfOpen = () => {
    const sidebarEl = document.getElementById("sidebar");
    const instance = bootstrap.Offcanvas.getInstance(sidebarEl);
    if (instance) {
      instance.hide();
    }
  };

  if (!user) {
    return (
      <div
        id="sidebar"
        className="offcanvas-lg offcanvas-start sidebar"
        data-bs-theme="dark"
        tabIndex="-1"
        style={{
          backgroundImage: "url('/images/layout_img/pattern_h.png')",
          backgroundRepeat: "repeat",
        }}
      >
        <div className="offcanvas-body p-0"></div>
      </div>
    );
  }

  return (
    <div
      id="sidebar"
      className="offcanvas-lg offcanvas-start sidebar"
      data-bs-theme="dark"
      tabIndex="-1"
      aria-labelledby="sidebarLabel"
    >
      <div className="offcanvas-header border-bottom border-secondary-subtle">
        <a href="/" id="sidebarLabel">
          <img
            className="sidebar-logo"
            src="/images/logo/logo_icon.png"
            alt="Logo"
          />
        </a>
        <button
          type="button"
          className="btn-close btn-close-white d-lg-none"
          data-bs-dismiss="offcanvas"
          data-bs-target="#sidebar"
          aria-label="Fermer"
        ></button>
      </div>

      <div className="offcanvas-body d-flex flex-column p-0">
        <div className="d-flex align-items-center gap-3 p-3 border-bottom border-secondary-subtle">
          <img
            className="rounded-circle sidebar-avatar"
            src={user?.photo ? urlProfile + user.photo : defaultProfile}
            alt="User"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = defaultProfile;
            }}
          />
          <div className="min-width-0">
            <h6 className="mb-0 text-truncate">
              {user.nom}
            </h6>
            <p className="sidebar-status mb-0 small d-flex align-items-center gap-1">
              <span className="online_animation"></span> Actif
            </p>
          </div>
        </div>

        <ul className="nav nav-pills flex-column p-2 gap-1 flex-grow-1 overflow-auto">
          {user.role !== 2 && (
            <>
              <li className="nav-item">
                <NavLink
                  className="nav-link d-flex align-items-center gap-2"
                  to="/users"
                  onClick={closeSidebarIfOpen}
                >
                  <i className="fa fa-users orange_color"></i>
                  <span>Utilisateurs</span>
                </NavLink>
              </li>

              <li className="nav-item">
                <NavLink
                  className="nav-link d-flex align-items-center gap-2"
                  to="/entreprises"
                  onClick={closeSidebarIfOpen}
                >
                  <i className="fa fa-building orange_color"></i>
                  <span>Entreprises</span>
                </NavLink>
              </li>

              <li className="nav-item">
                <NavLink
                  className="nav-link d-flex align-items-center gap-2"
                  to="/users-desactive"
                  onClick={closeSidebarIfOpen}
                >
                  <i className="fa fa-user-times orange_color"></i>
                  <span>Comptes désactivés</span>
                </NavLink>
              </li>

              <li className="nav-item">
                <NavLink
                  className="nav-link d-flex align-items-center gap-2"
                  to="/offres"
                  onClick={closeSidebarIfOpen}
                >
                  <i className="fa fa-credit-card orange_color"></i>
                  <span>Gestion offre</span>
                </NavLink>
              </li>
            </>
          )}

          {allowedLinks.map((link) => {
            const isAlertLink = link.path === "/liste_alert";

            return (
              <li className="nav-item" key={link.path}>
                <NavLink
                  className="nav-link d-flex align-items-center justify-content-between gap-2"
                  to={link.path}
                  onClick={() => {
                    if (isAlertLink) markAllAsRead();
                    closeSidebarIfOpen();
                  }}
                >
                  <span className="d-flex align-items-center gap-2">
                    <i className={`fa ${link.icon} orange_color`}></i>
                    <span>{link.label}</span>
                  </span>

                  {isAlertLink && unreadCount > 0 && (
                    <span className="badge rounded-pill bg-danger">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </NavLink>
              </li>
            );
          })}

          <li className="nav-item d-xl-none mt-auto">
            <button
              type="button"
              className="nav-link d-flex align-items-center gap-2 w-100 text-start bg-transparent border-0"
              onClick={(ev) => {
                ev.preventDefault();
                closeSidebarIfOpen();
                logout();
              }}
            >
              <i className="fa fa-power-off orange_color"></i>
              <span>Déconnexion</span>
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
}

export default Sidebar;
